import { BRAND_NAME, SUPPORT_EMAIL } from '@/config/brand';

// DRAFT, NOT LEGAL ADVICE: the facts the public legal pages (/privacy, /terms, /cookies) render.
// Every value in square brackets is a placeholder the owner must replace before launch (and before
// the pages are shown to Google or Amazon reviewers). The pages read these constants, so filling a
// value here updates it in every language at once.

/** The company that operates the product and is the party to the terms. */
export const LEGAL_ENTITY_NAME = 'Scopie LLC';

/**
 * The place shown after the entity name on the public pages (the contact page, /privacy, /terms and their structured data). CITY AND STATE ONLY, by the
 * owner's decision: his home street address must not appear anywhere public, and a test fails if a street number or a ZIP code is put here. A physical
 * postal address is required only in commercial / marketing emails (CAN-SPAM), not for transactional email; those are the backend's, and a PO box or
 * registered-agent address is what to use there.
 */
export const LEGAL_ADDRESS = 'Orlando, FL';

/** US state whose law governs the terms and whose courts hear disputes. */
export const GOVERNING_STATE = 'Florida';

/** County of the courts named in the terms' venue clause. */
export const VENUE_COUNTY = 'Orange County';

export { SUPPORT_EMAIL };

/** Draft date of the current text (ISO date, formatted per language on the page). */
export const LEGAL_LAST_UPDATED = '2026-10-10';

/** Staging runs on Hetzner Cloud; the production provider and region are not decided in the repo. */
export const HOSTING_PROVIDER = 'Hetzner Online GmbH';
export const HOSTING_REGION = 'Ashburn, Virginia, USA';

/** How long data is kept. The code has no purge for these yet. */
export const RETENTION_AFTER_CLOSURE = '90 days';
export const RETENTION_BACKUPS = '30 days';
export const RETENTION_SERVER_LOGS = '30 days';

/** Required by Google for apps that use Google sign-in. Keep this exact English sentence in every language. */
export const GOOGLE_LIMITED_USE_STATEMENT =
  `${BRAND_NAME}'s use and transfer to any other app of information received from Google APIs will adhere to the Google API Services User Data Policy, including the Limited Use requirements.`;

export const GOOGLE_USER_DATA_POLICY_URL = 'https://developers.google.com/terms/api-services-user-data-policy';

/** Values interpolated into the legal copy ({{entity}}, {{email}}, ...). */
export const LEGAL_COPY_VALUES = {
  entity: LEGAL_ENTITY_NAME,
  entityAndAddress: LEGAL_ADDRESS ? `${LEGAL_ENTITY_NAME}, ${LEGAL_ADDRESS}` : LEGAL_ENTITY_NAME,
  state: GOVERNING_STATE,
  venueCounty: VENUE_COUNTY,
  email: SUPPORT_EMAIL,
  hostingProvider: HOSTING_PROVIDER,
  hostingRegion: HOSTING_REGION,
  retentionAfterClosure: RETENTION_AFTER_CLOSURE,
  retentionBackups: RETENTION_BACKUPS,
  retentionLogs: RETENTION_SERVER_LOGS,
};

/** Keys whose value is empty or still holds a placeholder (anything in square brackets, such as a CONFIRM note; the image build also greps this file for one). */
export function unfilledValuesOf(values: Record<string, string>): string[] {
  return Object.entries(values)
    .filter(([, value]) => !value.trim() || /\[[^\]]*\]/.test(value))
    .map(([key]) => key);
}

/**
 * Legal values still unfilled. Empty once the owner has filled them in; the production build fails otherwise
 * (src/config/__tests__/legal-entity.test.ts runs in the gate of .github/workflows/deploy-production.yml).
 */
export function unfilledLegalValues(): string[] {
  return unfilledValuesOf(LEGAL_COPY_VALUES);
}

/** City and state shown after the company name in the site footer. */
export const LEGAL_PLACE = 'Orlando, FL';
